'use client';

import { createContext, useContext, useState, useSyncExternalStore } from 'react';
import type { AccordionItem } from '~/components/primitives/Accordion';

type SectionData = {
    items: AccordionItem[];
    sectionTitle: string;
};

type FaqSearchStore = {
    /** Register a section's items. Returns unregister fn. */
    register: (sectionId: string, data: SectionData) => () => void;
    /** Subscribe to changes */
    subscribe: (cb: () => void) => () => void;
    /** Get all items across all sections */
    getAllItems: () => AccordionItem[];
    /** Get snapshot version (for useSyncExternalStore) */
    getVersion: () => number;
    /** Is any search active */
    hasQuery: () => boolean;
    setHasQuery: (v: boolean) => void;
    /** Claim ownership of the search results display, unless another section holds it. */
    claimResultsOwner: (id: string) => void;
    /** The section that renders the search results, if any. */
    getResultsOwner: () => string | null;
    /** Release ownership of the search results display. */
    releaseResultsOwner: (id: string) => void;
};

function createFaqSearchStore(): FaqSearchStore {
    const sections = new Map<string, SectionData>();
    const listeners = new Set<() => void>();
    let _hasQuery = false;
    let _version = 0;
    let _resultsOwner: string | null = null;

    const notify = () => {
        _version++;
        listeners.forEach(cb => cb());
    };

    return {
        register(sectionId, data) {
            sections.set(sectionId, data);
            notify();
            return () => {
                sections.delete(sectionId);
                notify();
            };
        },
        subscribe(cb) {
            listeners.add(cb);
            return () => listeners.delete(cb);
        },
        getAllItems() {
            const all: AccordionItem[] = [];
            for (const data of sections.values()) {
                all.push(...data.items);
            }
            return all;
        },
        getVersion() {
            return _version;
        },
        hasQuery() {
            return _hasQuery;
        },
        setHasQuery(v) {
            if (_hasQuery !== v) {
                _hasQuery = v;
                notify();
            }
        },
        claimResultsOwner(id) {
            if (_resultsOwner === null) {
                _resultsOwner = id;
                notify();
            }
        },
        getResultsOwner() {
            return _resultsOwner;
        },
        releaseResultsOwner(id) {
            if (_resultsOwner === id) {
                _resultsOwner = null;
                notify();
            }
        },
    };
}

const FaqSearchContext = createContext<FaqSearchStore | null>(null);

export function FaqSearchProvider({ children }: { children: React.ReactNode }) {
    const [store] = useState(createFaqSearchStore);
    return <FaqSearchContext.Provider value={store}>{children}</FaqSearchContext.Provider>;
}

export function useFaqSearchStore() {
    return useContext(FaqSearchContext);
}

export function useFaqSearchResults(): { allItems: AccordionItem[]; hasQuery: boolean } {
    const store = useFaqSearchStore();
    const noop = () => () => {};

    // Subscribe to version changes to get fresh items
    const version = useSyncExternalStore(
        store?.subscribe ?? noop,
        () => store?.getVersion() ?? 0,
        () => 0,
    );

    const hasQuery = useSyncExternalStore(
        store?.subscribe ?? noop,
        () => store?.hasQuery() ?? false,
        () => false,
    );

    // version is used implicitly to trigger re-render
    void version;
    const allItems = store?.getAllItems() ?? [];

    return { allItems, hasQuery };
}

export function useFaqIsSearching(): boolean {
    const store = useFaqSearchStore();
    return useSyncExternalStore(
        store?.subscribe ?? (() => () => {}),
        () => store?.hasQuery() ?? false,
        () => false,
    );
}
